import React, { useState, useEffect, useRef, useCallback } from 'react'
import { AutoAwesome } from '@mui/icons-material'
import { Spin, Typography } from 'antd'
import { usePromiseTracker, trackPromise } from 'react-promise-tracker'
import services from '../../services'
import copilotLogo from '../../assets/img/ia/copilot-logo.png'
import { CaixaPergunta } from './Oneflow'

const { Text } = Typography

interface Message {
  sender: 'user' | 'ai';
  text: string;
}

const BOAS_VINDAS = 'Olá! Como posso analisar este dashboard para você?'

/**
 * A resposta do agente do Fabric vem em HTML. Ela roda num iframe só com scripts, sem a
 * origem do Portal: o HTML gerado pela IA não alcança cookies nem a sessão de quem está logado.
 */
const AiResponseMessage = ({ htmlContent }: { htmlContent: string }) => {
  const [iframeHeight, setIframeHeight] = useState('auto')
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    setIframeHeight('auto')
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      if (event.data && event.data.type === 'resize-iframe') setIframeHeight(`${event.data.height}px`)
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [htmlContent])

  const fullHtml = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <style>
      html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif; overflow: hidden; background-color: transparent; color: #555; line-height: 1.6; }
      canvas { max-width: 100%; height: auto !important; }
    </style>
  </head>
  <body>
    ${htmlContent}
    <script>
      const postHeight = () => parent.postMessage({ type: 'resize-iframe', height: document.documentElement.scrollHeight }, '*');
      window.addEventListener('load', postHeight);
      new ResizeObserver(postHeight).observe(document.body);
    </script>
  </body>
</html>`

  return (
    <iframe
      ref={iframeRef}
      srcDoc={fullHtml}
      style={{ width: '100%', height: iframeHeight, border: 'none', transition: 'height 0.2s ease-out' }}
      sandbox="allow-scripts"
      title="Resposta do oneflow"
    />
  )
}

/** O chat antigo, do agente do Fabric. Fica até os dashboards migrarem para o Snowflake. */
export default function ChatLegado({ dashId, environmentId }: { dashId: any; environmentId: any }) {
  const [inputValue, setInputValue] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const { promiseInProgress } = usePromiseTracker({ area: 'ai-response' })
  const fimRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [messages, promiseInProgress])

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim()) return

    const userMessage: Message = { sender: 'user', text: inputValue }
    setMessages(prev => [...prev, userMessage])
    const currentInput = inputValue
    setInputValue('')

    const currentMessages = [...messages, userMessage]
    const conversationPairs = []
    for (let i = 0; i < currentMessages.length - 1; i++) {
      if (currentMessages[i].sender === 'user' && currentMessages[i + 1].sender === 'ai') {
        conversationPairs.push({ user: currentMessages[i].text, ai: currentMessages[i + 1].text })
        i++
      }
    }

    const lastThreePairs = conversationPairs.slice(-3)
    let historyText = ''
    if (lastThreePairs.length > 0) {
      historyText = 'MENSAGENS ANTERIORES:\n\n'
      historyText += lastThreePairs.map((pair, index) => `${index + 1}- USER: "${pair.user}" - AI: "${pair.ai}"`).join('\n\n')
    }

    const finalPrompt = historyText ? `${historyText}\n\nPERGUNTA ATUAL: ${currentInput}` : currentInput
    const data = { prompt: finalPrompt, environmentId, dashboardId: dashId }

    try {
      const res = await trackPromise(services.api.sendAiAgentPrompt(data), 'ai-response')
      if (res.fail || !res.data.success) throw new Error(res.data?.response || res.error || 'Não foi possível obter uma resposta.')
      setMessages(prev => [...prev, { sender: 'ai', text: res.data.response }])
    } catch (error: any) {
      setMessages(prev => [...prev, { sender: 'ai', text: `Desculpe, ocorreu um erro: ${error.message}` }])
    }
  }, [inputValue, messages, environmentId, dashId])

  return (
    <>
      <div className="of-mensagens">
        <div className="of-msg of-msg-ia">
          <AutoAwesome className="of-msg-icone" />
          <div className="of-md"><p className="of-md-p">{BOAS_VINDAS}</p></div>
        </div>
        {messages.map((m, i) => m.sender === 'user'
          ? <div key={i} className="of-msg of-msg-pessoa"><div className="of-balao">{m.text}</div></div>
          : <div key={i} className="of-msg of-msg-ia"><AutoAwesome className="of-msg-icone" /><AiResponseMessage htmlContent={m.text} /></div>)}
        {promiseInProgress && (
          <div className="of-msg of-msg-ia of-linha">
            <AutoAwesome className="of-msg-icone" />
            <Spin size="small" />
            <Text type="secondary">Gerando...</Text>
          </div>
        )}
        <div ref={fimRef} />
      </div>
      <CaixaPergunta valor={inputValue} aoMudar={setInputValue} aoEnviar={handleSendMessage} ocupado={promiseInProgress} />
      <div className="of-rodape">
        <span>Criado com IA. É possível cometer erros.</span>
        <img style={{ width: 100 }} src={copilotLogo} alt="Copilot" />
      </div>
    </>
  )
}
